import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { LoadingBlock } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Lock, Smartphone, History, KeyRound, Fingerprint, FileDown, Trash2 } from "lucide-react";
import { formatIndianDateTime } from "@/lib/format";
import { toast } from "sonner";

export default function Security() {
    const { user, refresh } = useAuth();
    const [setup, setSetup] = useState(null);
    const [code, setCode] = useState("");
    const [pwd, setPwd] = useState("");
    const [busy, setBusy] = useState(false);
    const [audit, setAudit] = useState([]);
    const [panConsents, setPanConsents] = useState(null);  // null = loading, [] = empty, [...] = list

    const loadAudit = async () => {
        try {
            const { data } = await api.get("/audit?limit=80");
            setAudit(data);
        } catch (_e) {}
    };
    const loadPanConsents = async () => {
        try {
            const { data } = await api.get("/pan/consents");
            setPanConsents(data || []);
        } catch (_e) {
            setPanConsents([]);
        }
    };
    useEffect(() => { loadAudit(); loadPanConsents(); }, []);

    const downloadPdf = async (consentId) => {
        try {
            const res = await api.get(`/pan/consents/${consentId}/pdf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `lifevault-pan-snapshot-${consentId.slice(0, 8)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not download PDF");
        }
    };

    const revokeConsent = async (consentId) => {
        try {
            await api.delete(`/pan/consents/${consentId}`);
            toast.success("Consent revoked and snapshot purged");
            loadPanConsents();
            loadAudit();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to revoke");
        }
    };

    const startSetup = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/auth/2fa/setup");
            setSetup(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Setup failed");
        } finally {
            setBusy(false);
        }
    };

    const enable = async (e) => {
        e.preventDefault();
        if (!code || code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
        setBusy(true);
        try {
            await api.post("/auth/2fa/enable", { code });
            toast.success("2FA enabled");
            setSetup(null);
            setCode("");
            await refresh();
            loadAudit();
        } catch (e2) {
            toast.error(e2.response?.data?.detail || "Verification failed");
        } finally {
            setBusy(false);
        }
    };

    const disable = async () => {
        if (!pwd) { toast.error("Enter your password"); return; }
        setBusy(true);
        try {
            await api.post("/auth/2fa/disable", { password: pwd });
            toast.success("2FA disabled");
            setPwd("");
            await refresh();
            loadAudit();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="security-page">
            <PageHeader
                eyebrow="Security & Trust"
                title="Two-factor authentication & audit trail."
                subtitle="Add a second key. Watch every door open and close on your vault."
                testid="sec-header"
            />

            <div className="px-8 py-8 grid lg:grid-cols-3 gap-8 max-w-6xl">
                {/* 2FA card */}
                <section className="lg:col-span-2 rounded-lg border border-border bg-card p-8" data-testid="twofa-card">
                    <div className="flex items-center gap-3 mb-4">
                        <ShieldCheck className="h-6 w-6 text-primary" strokeWidth={1.5} />
                        <h2 className="font-display text-2xl font-bold tracking-tight">Two-factor authentication</h2>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed max-w-xl">
                        Use any authenticator app — Google Authenticator, Authy, 1Password, Aegis. Each
                        sign-in will require a 6-digit code from your phone.
                    </p>
                    <div className="mt-6 flex items-center gap-3">
                        <span className="label-tag">Status</span>
                        {user?.twofa_enabled ? (
                            <Badge className="bg-primary text-primary-foreground" data-testid="twofa-status">Enabled</Badge>
                        ) : (
                            <Badge variant="outline" data-testid="twofa-status">Not enabled</Badge>
                        )}
                    </div>

                    {!user?.twofa_enabled ? (
                        !setup ? (
                            <Button onClick={startSetup} disabled={busy} data-testid="twofa-setup-btn" className="mt-6 rounded-full">
                                <Smartphone className="mr-2 h-4 w-4" /> Set up 2FA
                            </Button>
                        ) : (
                            <form onSubmit={enable} className="mt-6 space-y-4 rounded-lg border border-border p-5 max-w-md" data-testid="twofa-setup-panel">
                                <p className="text-sm">1. Scan this QR with your authenticator app:</p>
                                <img
                                    src={`data:image/png;base64,${setup.qr_png_base64}`}
                                    alt="2FA QR"
                                    className="mx-auto bg-white p-2 rounded-md w-44 h-44"
                                />
                                <p className="text-xs text-muted-foreground text-center font-mono break-all">
                                    or enter the secret manually: <span className="select-all">{setup.secret}</span>
                                </p>
                                <p className="text-sm pt-2">2. Enter the 6-digit code shown in the app:</p>
                                <Input
                                    data-testid="twofa-code-input"
                                    inputMode="numeric" maxLength={6}
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                                    placeholder="000000"
                                    className="font-mono text-center tracking-[0.4em] text-lg"
                                />
                                <div className="flex items-center gap-2">
                                    <Button type="submit" disabled={busy} data-testid="twofa-enable-btn" className="rounded-full">
                                        <KeyRound className="mr-2 h-4 w-4" /> Verify & enable
                                    </Button>
                                    <Button type="button" variant="ghost" onClick={() => setSetup(null)}>Cancel</Button>
                                </div>
                            </form>
                        )
                    ) : (
                        <div className="mt-6 space-y-3 rounded-lg border border-border p-5 max-w-md" data-testid="twofa-disable-panel">
                            <Label htmlFor="pwd">Confirm your password to disable 2FA</Label>
                            <Input
                                id="pwd" type="password"
                                data-testid="twofa-disable-pwd"
                                value={pwd} onChange={(e) => setPwd(e.target.value)}
                                placeholder="••••••••"
                            />
                            <Button onClick={disable} disabled={busy} variant="outline" data-testid="twofa-disable-btn">
                                <Lock className="mr-2 h-4 w-4" /> Disable 2FA
                            </Button>
                        </div>
                    )}
                </section>

                {/* Audit log */}
                <aside className="rounded-lg border border-border bg-card p-6" data-testid="audit-card">
                    <div className="flex items-center gap-3 mb-4">
                        <History className="h-5 w-5 text-primary" strokeWidth={1.5} />
                        <h2 className="font-display text-lg font-semibold">Recent activity</h2>
                    </div>
                    {audit.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No events yet.</p>
                    ) : (
                        <ul className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                            {audit.map((a, i) => (
                                <li key={a.id || i} className="text-sm" data-testid={`audit-${i}`}>
                                    <div className="font-mono text-xs">{a.action}</div>
                                    <div className="text-xs text-muted-foreground">
                                        {(a.ts || "").slice(0, 19).replace("T", " ")}
                                        {a.ip ? ` · ${a.ip}` : ""}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </aside>

                {/* PAN consent log */}
                <section className="lg:col-span-3 rounded-lg border border-border bg-card p-8" data-testid="pan-consent-log">
                    <div className="flex items-center gap-3 mb-4">
                        <Fingerprint className="h-6 w-6 text-primary" strokeWidth={1.5} />
                        <h2 className="font-display text-2xl font-bold tracking-tight">PAN snapshot consents</h2>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
                        Every PAN financial snapshot you authorise is logged here with a tamper-evident hash.
                        Download the report as a PDF for your records, or revoke a consent — revoking
                        permanently purges the cached snapshot blob from our database.
                    </p>
                    {panConsents === null ? (
                        <p className="mt-6 text-sm text-muted-foreground" data-testid="pan-consent-loading">
                            Loading consents…
                        </p>
                    ) : panConsents.length === 0 ? (
                        <p className="mt-6 text-sm text-muted-foreground" data-testid="pan-consent-empty">
                            No PAN snapshots yet. Generate one from PAN Snapshot in the sidebar.
                        </p>
                    ) : (
                        <ul className="mt-6 space-y-3" data-testid="pan-consent-list">
                            {panConsents.map((c, i) => (
                                <li
                                    key={c.consent_id}
                                    className="rounded-md border border-border p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                                    data-testid={`pan-consent-${i}`}
                                >
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-mono text-sm">{c.consent_id}</span>
                                            {c.revoked ? (
                                                <Badge variant="outline" className="border-destructive/40 text-destructive">Revoked</Badge>
                                            ) : (
                                                <Badge className="bg-primary text-primary-foreground">Active</Badge>
                                            )}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">
                                            {formatIndianDateTime(c.consented_at)} · scope: {(c.scope || []).join(", ") || "—"}
                                        </div>
                                        <div className="text-[10px] text-muted-foreground font-mono mt-0.5 truncate">
                                            hash: {(c.consent_hash || "").slice(0, 32)}…
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {!c.revoked && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => downloadPdf(c.consent_id)}
                                                data-testid={`pan-pdf-btn-${i}`}
                                            >
                                                <FileDown className="mr-2 h-4 w-4" /> PDF
                                            </Button>
                                        )}
                                        {!c.revoked && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => revokeConsent(c.consent_id)}
                                                data-testid={`pan-revoke-btn-${i}`}
                                                className="text-destructive hover:text-destructive"
                                            >
                                                <Trash2 className="mr-2 h-4 w-4" /> Revoke
                                            </Button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                {/* Tip */}
                <div className="lg:col-span-3 text-xs text-muted-foreground">
                    <LoadingBlockHidden /> {/* keeps import used; harmless */}
                    Audit events are immutable and retained for the lifetime of the account. Sensitive
                    fields (passwords, secrets, file contents) are never logged.
                </div>
            </div>
        </div>
    );
}

function LoadingBlockHidden() { return null; }
